import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import CodeMirror from '@uiw/react-codemirror'
import { python } from '@codemirror/lang-python'
import { githubLight } from '@uiw/codemirror-theme-github'
import { Alert, CircularProgress } from '@mui/material'

import Markdown from '../../study/components/Markdown'
import { showToast } from '../commonUnit/Toast'
import { getReasoningGapContent, saveReasoningGapContent } from '../../service/reasoningGapService'
import { REASONING_GAP_TYPES } from './reasoningGapConfig'

// Exercise Editor tab of a Reasoning Gap session - same view/Edit Exercise/Save pattern as
// the GUI exercise editor (GuiExerciseEditor.jsx), reusing its styles.
//
// Only the task text and the visible examples are editable. Each example's expected output
// and the students' starter code are computed server-side by the reference solution, so
// they're shown here as generated, read-only values. The explanation prompt, hidden tests and the
// feedback rubric are fixed (both study conditions must stay comparable) and are listed
// read-only so the instructor can see exactly what students get.

const EXAMPLE_PREVIEW_DELAY_MS = 400

const listText = values => (values || []).join(', ')
const pyTuple = values => `(${(values || []).map((v) => {
  const rounded = Number(v.toFixed(6))
  return Number.isInteger(rounded) ? rounded.toFixed(1) : String(rounded)
}).join(', ')})`

// "1, 2, 3" or "1 2 3" -> [1, 2, 3]; null if any entry isn't a number.
function parseList (text) {
  const values = text.split(/[\s,]+/).filter(Boolean).map(Number)
  return values.length && values.every(Number.isFinite) ? values : null
}

// Each quiz describes its example fields (content.exampleFields): a "list" is one line of
// numbers, a "matrix" is one row of numbers per line (e.g. W1, one row per hidden neuron).
const fieldText = (field, value) => (field.kind === 'matrix' ? (value || []).map(listText).join('\n') : listText(value))
function parseField (field, text) {
  if (field.kind !== 'matrix') return parseList(text)
  const rows = text.split(/\n|;/).map(line => line.trim()).filter(Boolean).map(parseList)
  return rows.length && rows.every(Boolean) ? rows : null
}
const toRows = (examples, fields) => examples.map(e => Object.fromEntries(fields.map(f => [f.key, fieldText(f, e[f.key])])))
// Which example an error message is about ("Example 3: ..."), or -1.
const errorIndexOf = message => {
  const match = /^Example (\d+):/.exec(message || '')
  return match ? Number(match[1]) - 1 : -1
}
const pick = (examples, fields) => examples.map(e => Object.fromEntries(fields.map(f => [f.key, e[f.key]])))
const sameExamples = (a, b, fields) => JSON.stringify(pick(a, fields)) === JSON.stringify(pick(b, fields))

// Parses every row; returns { examples } or { error } naming the first bad row.
function parseRows (rows, fields) {
  const examples = []
  for (let i = 0; i < rows.length; i++) {
    const example = {}
    for (const f of fields) {
      const value = parseField(f, rows[i][f.key] || '')
      if (!value) {
        return { error: `Example ${i + 1}: enter ${f.key} as numbers separated by commas${f.kind === 'matrix' ? ', one row per line' : ''}.` }
      }
      example[f.key] = value
    }
    examples.push(example)
  }
  return { examples }
}

export default function ReasoningGapExerciseEditor ({ session, submissionCount, onEditingChange, onContentChanged }) {
  const [content, setContent] = useState(null)
  const [loadError, setLoadError] = useState('')
  const [isEditable, setIsEditable] = useState(false)
  const [intro, setIntro] = useState('')
  const [rows, setRows] = useState([]) // [{ [field key]: text }] while editing
  const [draft, setDraft] = useState(null) // server-rendered preview of the unsaved edits
  const [draftError, setDraftError] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const fields = content?.exampleFields || []

  const load = useCallback(async () => {
    try {
      setContent(await getReasoningGapContent(session.id))
      setLoadError('')
    } catch (e) {
      setLoadError(e.response?.data?.error || e.message)
    }
  }, [session.id])

  // Re-check the lock whenever the submission count changes (the page polls it).
  useEffect(() => { load() }, [load, submissionCount])
  useEffect(() => { onEditingChange && onEditingChange(isEditable) }, [isEditable, onEditingChange])

  // Live preview of each example's expected output + the starter code while editing.
  const previewTimer = useRef(null)
  useEffect(() => {
    if (!isEditable) return undefined
    clearTimeout(previewTimer.current)
    previewTimer.current = setTimeout(async () => {
      const parsed = parseRows(rows, fields)
      if (parsed.error) {
        setDraftError(parsed.error)
        return
      }
      try {
        setDraft(await saveReasoningGapContent(session.id, { intro, examples: parsed.examples, dryRun: true }))
        setDraftError('')
      } catch (e) {
        setDraftError(e.response?.data?.error || e.message)
      }
    }, EXAMPLE_PREVIEW_DELAY_MS)
    return () => clearTimeout(previewTimer.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditable, rows, intro, session.id])

  const updateRow = (index, patch) => setRows(current => current.map((r, i) => i === index ? { ...r, ...patch } : r))
  // A new example starts as a copy of the previous one with its last field blank (the
  // examples usually share their inputs; e.g. CART keeps x and asks for new y).
  const addRow = () => setRows((current) => {
    const last = current[current.length - 1] || {}
    return [...current, Object.fromEntries(fields.map((f, i) => [f.key, i < fields.length - 1 ? (last[f.key] || '') : '']))]
  })
  const removeRow = index => setRows(current => current.filter((_, i) => i !== index))

  const handleEditClick = () => {
    setIntro(content.intro)
    setRows(toRows(content.examples, fields))
    setDraft(content)
    setDraftError('')
    setIsEditable(true)
  }

  const handleCancel = () => {
    setIsEditable(false)
    setDraft(null)
    setDraftError('')
  }

  const handleRestoreOriginal = () => {
    setIntro(content.defaults.intro)
    setRows(toRows(content.defaults.examples, fields))
  }

  // Switching quizzes (before anyone submits) starts from the new quiz's defaults and clears
  // this session's group pairing, since both sessions of a pair must use the same quiz.
  const handleQuizChange = async (problemId) => {
    if (problemId === content.problemId) return
    const title = content.problemChoices.find(p => p.id === problemId)?.title || problemId
    if (!window.confirm(`Switch this session to "${title}"? The task text and examples reset to that quiz's defaults${content.pairedWith ? ', and the random group link for this session stops working until you pair it again' : ''}.`)) return
    setIsSaving(true)
    try {
      setContent(await saveReasoningGapContent(session.id, { problemId }))
      onContentChanged && onContentChanged()
      showToast(`This session now uses "${title}".`, 'success', 2500)
    } catch (e) {
      showToast(e.response?.data?.error || e.message, 'error')
    } finally {
      setIsSaving(false)
    }
  }

  const handleSave = async () => {
    const parsed = parseRows(rows, fields)
    if (!intro.trim()) return showToast('The task text cannot be empty.', 'error')
    if (parsed.error) return showToast(parsed.error, 'error')
    const isOriginal = intro.trim() === content.defaults.intro.trim() && sameExamples(parsed.examples, content.defaults.examples, fields)
    setIsSaving(true)
    try {
      const saved = await saveReasoningGapContent(session.id, isOriginal ? { reset: true } : { intro, examples: parsed.examples })
      setContent(saved)
      onContentChanged && onContentChanged()
      setIsEditable(false)
      setDraft(null)
      showToast(isOriginal ? 'Restored the original quiz.' : 'Exercise saved. Students will see the new version.', 'success', 2500)
    } catch (e) {
      showToast(e.response?.data?.error || e.message, 'error')
    } finally {
      setIsSaving(false)
    }
  }

  if (loadError) return <Alert severity="error">{loadError}</Alert>
  if (!content) return <CircularProgress size={22} />

  const shown = isEditable && draft ? draft : content
  const typeInfo = REASONING_GAP_TYPES[session.type]
  const otherType = Object.values(REASONING_GAP_TYPES).find(t => t !== typeInfo)

  return (
    <div className="rg-exercise">
      <div className="gui-exercise-editor-header">
        <h3>Reasoning Gap Exercise: {typeInfo?.condition === 'code' ? 'Code' : 'Thinking'} Ground Truth</h3>
        {isEditable ? (
          <div className="gui-exercise-editor-actions">
            <button className="gui-exercise-btn" onClick={handleRestoreOriginal} disabled={isSaving}>Restore original quiz</button>
            <button className="gui-exercise-btn" onClick={handleCancel} disabled={isSaving}>Cancel</button>
            <button className="gui-exercise-btn gui-exercise-btn-primary" onClick={handleSave} disabled={isSaving || !!draftError}>
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        ) : (
          <button
            className="gui-exercise-btn"
            onClick={handleEditClick}
            disabled={content.locked}
            title={content.locked ? 'Locked because students have already submitted' : undefined}
          >
            {content.locked ? 'Editing locked' : 'Edit Exercise'}
          </button>
        )}
      </div>

      <p className="gui-exercise-note">
        Students write <code>{content.functionName}</code> in Python, explain their thinking in their own words, can ask
        the built-in AI assistant questions while they work, and submit. After each submission they see how many hidden inputs their code handled and up to three
        hints about gaps in their code or explanation, never the solution. They rate the hints, then can
        revise and resubmit (up to 5 attempts in all, each attempt kept for you) or finish with a short
        survey on AI use.
        {' '}
        {content.locked
          ? <strong>This exercise is locked because {content.submissionCount} student{content.submissionCount === 1 ? ' has' : 's have'} already submitted. Everyone in a session works from the same version.</strong>
          : <>You can edit the task and examples until the first student submits; the rest is fixed so the two study conditions stay comparable.</>}
      </p>

      <h4 className="gui-exercise-subtitle">Quiz</h4>
      {content.locked || isEditable ? (
        <p className="gui-exercise-note"><strong>{content.problemTitle}</strong>{content.locked ? ' (fixed now that students have submitted)' : ''}</p>
      ) : (
        <p className="gui-exercise-note">
          <select className="rg-exercise__quiz" value={content.problemId} onChange={e => handleQuizChange(e.target.value)} disabled={isSaving} aria-label="Quiz">
            {content.problemChoices.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
          {' '}Both sessions of a study pair must use the same quiz. Changing it resets the task text and examples to that quiz's defaults.
        </p>
      )}

      <h4 className="gui-exercise-subtitle">Study condition</h4>
      <p className="gui-exercise-note">
        Hints in this session are generated by comparing each submission against the
        {' '}<strong>{typeInfo?.condition === 'code' ? 'reference code' : 'reference reasoning (the Thought Chain)'}</strong>.
        {otherType && <> The other condition is the <em>{otherType.label}</em> session type. Create one session of each and give each group its own link.</>}
      </p>

      <h4 className="gui-exercise-subtitle">
        Task{content.customized && !isEditable && <em className="gui-exercise-file-tag"> (customized, revision {content.revision})</em>}
      </h4>
      {isEditable ? (
        <>
          <textarea
            className="gui-exercise-textarea rg-exercise__mono"
            value={intro}
            onChange={e => setIntro(e.target.value)}
            rows={16}
          />
          <p className="gui-exercise-note">
            Markdown: <code>##</code> headings, <code>`code`</code>, <code>**bold**</code>, and <code>```</code> code blocks.
            Keep the rules consistent with the hidden tests below.
          </p>
        </>
      ) : (
        <div className="gui-exercise-description rg-exercise__rendered">
          <Markdown text={content.intro} />
        </div>
      )}

      <h4 className="gui-exercise-subtitle">Examples</h4>
      <p className="gui-exercise-note">
        Shown to students below the task, and run by the starter code so students can compare their
        output with each expected result. Expected results are computed from your values by the
        reference solution. These are visible practice examples; grading uses the hidden tests below.
      </p>
      {isEditable ? (
        <div className="rg-exercise__examples">
          {rows.map((row, i) => (
            <div key={i} className="rg-exercise__example">
              <div className="rg-exercise__example-head">
                <span className="gui-exercise-file-label">Example {i + 1}</span>
                {rows.length > 1 && (
                  <button className="gui-exercise-btn rg-exercise__remove" onClick={() => removeRow(i)} aria-label={`Remove example ${i + 1}`}>Remove</button>
                )}
              </div>
              {fields.map(f => (
                <Fragment key={f.key}>
                  <label className="gui-exercise-file-label" htmlFor={`rg-example-${i}-${f.key}`}>{f.label}</label>
                  {f.kind === 'matrix'
                    ? <textarea id={`rg-example-${i}-${f.key}`} className="gui-exercise-test-name rg-exercise__mono" rows={Math.max(2, (row[f.key] || '').split('\n').length)} value={row[f.key] || ''} onChange={e => updateRow(i, { [f.key]: e.target.value })} placeholder={f.placeholder} />
                    : <input id={`rg-example-${i}-${f.key}`} className="gui-exercise-test-name" value={row[f.key] || ''} onChange={e => updateRow(i, { [f.key]: e.target.value })} placeholder={f.placeholder} />}
                </Fragment>
              ))}
              <span className="gui-exercise-file-label">Expected result</span>
              {/* Other examples keep their last computed result while one is being fixed. */}
              {draft?.examples?.[i] && !(draftError && (errorIndexOf(draftError) === i || errorIndexOf(draftError) === -1))
                ? <code className="gui-exercise-test-code">{draft.examples[i].expectedText || pyTuple(draft.examples[i].expected)}</code>
                : <span className="rg-muted">-</span>}
            </div>
          ))}
          {draftError && <span className="rg-exercise__error">{draftError}</span>}
          <div>
            <button className="gui-exercise-btn" onClick={addRow} disabled={rows.length >= content.maxExamples}>+ Add example</button>
          </div>
        </div>
      ) : (
        <pre className="gui-exercise-test-code rg-exercise__block">{content.examples.map((e, i) => `# Example ${i + 1}
${fields.map(f => `${f.key} = ${f.kind === 'matrix' ? `[${(e[f.key] || []).map(r => `[${listText(r)}]`).join(', ')}]` : `[${listText(e[f.key])}]`}`).join('\n')}
expected ≈ ${e.expectedText || pyTuple(e.expected)}`).join('\n\n')}</pre>
      )}

      <h4 className="gui-exercise-subtitle">Starter code <em className="gui-exercise-file-tag">(generated from the examples)</em></h4>
      <div className="gui-exercise-file">
        <CodeMirror value={shown.preview.starterCode} theme={githubLight} extensions={[python()]} height="170px" editable={false} />
      </div>

      <h4 className="gui-exercise-subtitle">Explanation prompt</h4>
      <div className="gui-exercise-description">{content.explanationPrompt}</div>

      <h4 className="gui-exercise-subtitle">AI assistant</h4>
      <p className="gui-exercise-note">
        A chat panel beside the editor answers questions while students work and between attempts; it pauses while an attempt is being checked and closes when they finish. Each
        question is sent with the student's current code and explanation. It guides one idea at a time,
        explains concepts and Python, and points at problems, but never writes the solution. Like the
        hints, it is grounded in this session's {content.condition === 'code' ? 'reference code (answers point at the student\'s code)' : 'reference reasoning (answers point at reasoning steps)'}.
        Every conversation appears in the Submissions tab and in Export Data.
      </p>

      <h4 className="gui-exercise-subtitle">Hidden tests</h4>
      <p className="gui-exercise-note">
        Run in the student's browser right after they submit. Students only see how many passed; the
        results also inform the hints.
      </p>
      <ul className="rg-exercise__list">
        {content.tests.map(t => <li key={t.id}>{t.description}</li>)}
      </ul>

      <h4 className="gui-exercise-subtitle">Feedback</h4>
      <p className="gui-exercise-note">
        Each submission's code and explanation are judged separately on these reasoning steps
        ({content.analysisModel.model}, prompt {content.analysisModel.promptVersion}). Hints point
        students toward the steps they missed without giving away the answer.
      </p>
      <ol className="rg-exercise__list">
        {content.rubric.map(u => <li key={u.id}>{u.title}</li>)}
      </ol>
    </div>
  )
}
